import { createEvent, createStore } from 'effector';
import type { DatumEngine } from '@datum-sdk/engine';
import type { DevEditorTools } from '../scene/sdk/devEditorTools';
import type { SceneConfig } from '../scene/sceneConfig';
import type { Sight } from '../types';
import { sceneLoadingReset } from './sceneLoading';

/**
 * Dev-only handles for the SDK dev panels (`SplatDevPanel` under `?dev`,
 * `FlightEditorPanel` under `?flight`). Published by `createSdkScene` ONLY in
 * dev mode (`import.meta.env.DEV || ?dev` — same gate as the legacy in-scene
 * DevVisuals) or under `?flight`, so in production without those flags this
 * store is always null and the panels never mount anything live.
 *
 * Deliberately the RAW engine handle rather than a curated facade: the panel
 * is a tuning tool that needs most of the public engine surface (camera state,
 * collider mutation and debug views, POI upsert, appearance config, runtime
 * options), and a dev-gated store keeps that power out of the production paths
 * without inventing a parallel API.
 *
 * Reset on every navigation (`sceneLoadingReset`) and nulled explicitly on
 * dispose — consumers must tolerate the handle vanishing mid-frame (null
 * checks, cleanup effects keyed on the store value).
 */
export interface SplatDevHandles {
  engine: DatumEngine;
  config: SceneConfig;
  sights: Sight[];
  /**
   * The adapter-computed render settings the engine was constructed with
   * (current device class + tier, incl. the pinned maxPixelRatio /
   * motionPixelRatioScale / minSortIntervalMs). Seeds the panel's Render
   * Settings block; live edits go through
   * `engine.setRuntimeOptions({ renderSettings })` (runtime layer wins the
   * merge chain). Undefined when no quality profile was resolved.
   */
  renderSettings?: Record<string, number>;
  /**
   * Route editor, route player and orbit-pivot marker from `@datum-sdk/editor`.
   * Scene-owned: torn down with the scene, before the engine.
   */
  editorTools: DevEditorTools;
}

export const splatDevHandlesReady = createEvent<SplatDevHandles | null>();

export const $splatDevHandles = createStore<SplatDevHandles | null>(null)
  .on(splatDevHandlesReady, (_, handles) => handles)
  .reset(sceneLoadingReset);
